import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { ListGroup } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';

import Sidebar from '../components/Sidebar';
import NavBar from '../components/NavBar';
import MenuLink from '../components/MenuLink';
import NotificationBar from '../components/NotificationBar';
import '../components/NotificationBar/styles.scss';

import logo from '../assets/logo.svg';
import logo_white from '../assets/logo-white.svg';
import MenuSeparator from '../pages/CompanyAdmin/components/MenuSeparator';
import { END_USERS_ROLE } from '../utils/constants';
import { AlertToastContainer } from '../components/AlertToastContainer';
import 'react-toastify/dist/ReactToastify.css';
import LogOutLink from '../components/LogOutLink';
import './styles.scss';

import { handleLogoNavigation } from './helper/appContainer';
import { useStore } from '../store';

const AppContainer = observer(({ children }) => {
  const {
    rootStore,
    rootStore: { profileStore },
  } = useStore();
  const { t } = useTranslation('account');

  const [showNotificationBar, setShowNotificationBar] = useState(false);
  const [width, setWidth] = useState(0);

  const history = useHistory();

  useEffect(() => {
    if (!profileStore.connection) {
      history.push('/');
    }
  }, [history, profileStore.connection]);

  useEffect(() => {
    const setWindowDimensions = () => {
      setWidth(window.innerWidth);
    };

    setWindowDimensions();
    window.addEventListener('resize', setWindowDimensions);

    return () => {
      window.removeEventListener('resize', setWindowDimensions);
    };
  }, []);

  const setNotificationBarState = showNotificationBar => {
    setShowNotificationBar(showNotificationBar);
  };

  const isEndUser = profileStore.connection === END_USERS_ROLE;
  const isCompanyAdmin = profileStore.isOwner && profileStore.companyAddress !== '';
  const isSuperAdmin = profileStore.isSuperAdmin;

  const isDarkMode =
    window.matchMedia('(prefers-color-scheme: dark)').matches || window.matchMedia('(color-scheme: dark)').matches;

  return (
    <>
      <AlertToastContainer position="top-right" autoClose={6000} pauseOnHover hideProgressBar={true} />
      {showNotificationBar && (
        <NotificationBar
          companyId={profileStore.company?.id}
          isCompanyAdmin={isCompanyAdmin}
          isSuperAdmin={isSuperAdmin}
          isEndUser={isEndUser}
          setNotificationBarState={setNotificationBarState}
        />
      )}

      <div
        className="styled-hr-wrapper"
        style={{
          display: width <= 748 && !showNotificationBar ? 'block' : 'none',
        }}
      >
        <hr className="styled-hr-line" />
      </div>

      <NavBar rootStore={rootStore} />
      <Sidebar id="sidebar-wrapper">
        <div className="sidebar-heading">
          <Link to={handleLogoNavigation(profileStore.connection)}>
            <img src={isDarkMode ? logo_white : logo} alt="Feller" />
          </Link>
        </div>

        <ListGroup flush className="mt-5">
          <MenuLink to="/welcome" exact isAllowed={!isEndUser}>
            {t('menu.welcome')}
          </MenuLink>
          <MenuLink to="/" exact isAllowed={true}>
            {t('menu.profile')}
          </MenuLink>
          <MenuLink to="/company" isAllowed={!isEndUser}>
            {t('menu.company')}
          </MenuLink>
          <MenuLink to="/account_settings" isAllowed={true}>
            {t('menu.accountSettings')}
          </MenuLink>
          <MenuLink to="/devices" isAllowed={isEndUser}>
            {t('menu.devices')}
          </MenuLink>
          <MenuLink to="/user_activities" isAllowed={isEndUser}>
            {t('menu.userActivities')}
          </MenuLink>
          <MenuLink to="/newsletters" isAllowed={true}>
            {t('menu.newsletters')}
          </MenuLink>
          <MenuLink to="/courses" isAllowed={!isEndUser}>
            {t('menu.courses')}
          </MenuLink>
        </ListGroup>
        {isCompanyAdmin && <MenuSeparator />}
        {isCompanyAdmin && (
          <ListGroup flush className="mt-2">
            <MenuLink to="/company_admin" exact isAllowed={isCompanyAdmin}>
              {t('menu.company_admin')}
            </MenuLink>
            <MenuLink to="/company_users" isAllowed={isCompanyAdmin}>
              {t('menu.company_admin_users')}
            </MenuLink>
          </ListGroup>
        )}
        {!profileStore.isNavigatedFromMobile && <LogOutLink rootStore={rootStore} />}
      </Sidebar>
      <div className="page-content-wrapper">{children}</div>
    </>
  );
});
export default AppContainer;
